import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useLocation, Link } from "react-router-dom";
import { Building2, Home, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AllDemoCredentials } from "./Login";
import { ForgotPasswordDialog, ForgotPasswordLink } from "../components/ForgotPasswordDialog";
import { CitizenOtpStep } from "../components/CitizenOtpStep";   // Phase 206

export default function CitizenAuth() {
  const [munis, setMunis] = useState([]);
  const [params] = useSearchParams();
  const [muniCode, setMuniCode] = useState(params.get("muni") || "");
  const [form, setForm] = useState({ email: "", password: "", name: "", mobile: "" });
  const [loading, setLoading] = useState(false);
  const [fpOpen, setFpOpen] = useState(false);
  const [otpChallenge, setOtpChallenge] = useState(null);   // Phase 206: register → OTP step
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/public/municipalities").then((r) => {
      setMunis(r.data);
      if (!muniCode && r.data.length === 1) setMuniCode(r.data[0].code);
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handlePick = (c) => {
    if (c.kind !== "citizen") {
      navigate("/login", { state: { demo: c } });
      return;
    }
    if (c.muni) setMuniCode(c.muni);
    setForm((f) => ({ ...f, email: c.email, password: c.password }));
    toast.info(`Filled ${c.role} (${(c.muni || "").toUpperCase()}) credentials — press Login`);
  };

  const location = useLocation();
  useEffect(() => {
    if (location.state?.demo) handlePick(location.state.demo);
    // eslint-disable-next-line
  }, []);

  const submit = (mode) => async (e) => {
    e.preventDefault();
    if (!muniCode) return toast.error("Select your municipality");
    setLoading(true);
    try {
      if (mode === "register") {
        // Phase 206: verify the mobile by OTP before the account is created
        if (!/^[6-9]\d{9}$/.test(form.mobile)) { toast.error("Enter a valid 10-digit Indian mobile number"); return; }
        const r = await api.post(`/t/${muniCode}/auth/register/send-otp`, { mobile: form.mobile, name: form.name });
        setOtpChallenge(r.data);
        toast.success(r.data.demo ? "Verification code generated" : `Verification code sent to ${r.data.mobile_hint}`);
        return;
      }
      const r = await api.post(`/t/${muniCode}/auth/login`, { email: form.email, password: form.password });
      login({ token: r.data.token, user: r.data.user, municipality: r.data.municipality });
      toast.success(`Welcome, ${r.data.user.name}`);
      navigate(`/${muniCode}/portal`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  // Phase 206: OTP verified → create the account with the proof → onboarding
  const completeRegistration = async ({ challenge_id, proof }) => {
    const r = await api.post(`/t/${muniCode}/auth/register`, {
      name: form.name, email: form.email, mobile: form.mobile, password: form.password, otp_challenge_id: challenge_id, otp_proof: proof,
    });
    login({ token: r.data.token, user: r.data.user, municipality: r.data.municipality });
    toast.success(`Account created — welcome, ${r.data.user.name}`);
    // Phase 205: new citizens go straight to "Link Your Properties" (they can skip and add more later)
    navigate(`/${muniCode}/portal/link-properties?onboarding=1`);
  };

  return (
    <div className="min-h-screen civic-mist flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <Button asChild variant="ghost" size="sm" className="h-9 px-2.5 -ml-2.5" data-testid="citizen-home-link">
            <Link to="/"><Home className="h-4 w-4 mr-1.5" /> Home</Link>
          </Button>
          <Button asChild variant="ghost" size="sm" className="h-9 px-2.5 text-muted-foreground" data-testid="citizen-staff-login-link">
            <Link to="/login">Staff login <ArrowRight className="h-3.5 w-3.5 ml-1" /></Link>
          </Button>
        </div>
        <div className="flex items-center gap-2 justify-center mb-6 cursor-pointer" onClick={() => navigate("/")}>
          <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center">
            <Building2 className="h-6 w-6 text-primary-foreground" />
          </div>
          <div>
            <p className="font-semibold font-heading">Citizen Portal</p>
            <p className="text-xs text-muted-foreground">MunicipalConnect</p>
          </div>
        </div>
        <Card className="shadow-md">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg">Citizen access</CardTitle>
            <CardDescription>Login or create a new citizen account</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5 mb-4">
              <Label>Municipality</Label>
              <Select value={muniCode} onValueChange={setMuniCode}>
                <SelectTrigger data-testid="citizen-municipality-select">
                  <SelectValue placeholder="Select municipality" />
                </SelectTrigger>
                <SelectContent>
                  {munis.map((m) => (
                    <SelectItem key={m.code} value={m.code}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Tabs defaultValue="login">
              <TabsList className="grid grid-cols-2 w-full">
                <TabsTrigger value="login" data-testid="tab-citizen-login">Login</TabsTrigger>
                <TabsTrigger value="register" data-testid="tab-citizen-register">Register</TabsTrigger>
              </TabsList>
              <TabsContent value="login">
                <form onSubmit={submit("login")} className="space-y-4 mt-2">
                  <div className="space-y-1.5">
                    <Label>Email or Mobile</Label>
                    <Input value={form.email} onChange={set("email")} required data-testid="citizen-login-email-input" />
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label>Password</Label>
                      <ForgotPasswordLink onClick={() => setFpOpen(true)} testId="citizen-forgot-password-link" />
                    </div>
                    <Input type="password" value={form.password} onChange={set("password")} required data-testid="citizen-login-password-input" />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="citizen-login-submit-button">
                    {loading ? "Signing in..." : "Login"}
                  </Button>
                </form>
                <AllDemoCredentials onPick={handlePick} testId="citizen-all-demo-credentials" />
              </TabsContent>
              <TabsContent value="register">
                {otpChallenge ? (
                  <CitizenOtpStep muniCode={muniCode} challenge={otpChallenge} onVerified={completeRegistration}
                    onBack={() => setOtpChallenge(null)} busyLabel="Creating account…" />
                ) : (
                <form onSubmit={submit("register")} className="space-y-4 mt-2" data-testid="citizen-register-form">
                  <p className="text-xs text-muted-foreground">Step 1 of 2 — create your account. We will verify your mobile with a one-time code, then you will link your properties by PIN.</p>
                  <div className="space-y-1.5">
                    <Label htmlFor="citizen-register-name">Full Name</Label>
                    <Input id="citizen-register-name" value={form.name} onChange={set("name")} required autoComplete="name" data-testid="citizen-register-name-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="citizen-register-email">Email</Label>
                    <Input id="citizen-register-email" type="email" value={form.email} onChange={set("email")} required autoComplete="email" data-testid="citizen-register-email-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="citizen-register-mobile">Mobile Number (10 digits)</Label>
                    <Input id="citizen-register-mobile" inputMode="numeric" pattern="[6-9][0-9]{9}" title="10-digit Indian mobile number"
                      value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                      maxLength={10} required autoComplete="tel-national" data-testid="citizen-register-mobile-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="citizen-register-password">Password</Label>
                    <Input id="citizen-register-password" type="password" value={form.password} onChange={set("password")} required minLength={6} autoComplete="new-password" data-testid="citizen-register-password-input" />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="citizen-register-submit-button">
                    {loading ? "Sending code..." : "Send verification code"}
                  </Button>
                </form>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
        <ForgotPasswordDialog open={fpOpen} onOpenChange={setFpOpen}
          variant="tenant" muniCode={muniCode} defaultEmail={form.email} />
      </div>
    </div>
  );
}
